import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * The type-atom family, from the TYPE ATOMS block of `mobile/atoms.css`.
 *
 * CSS selectors: `.ds-kicker`, `.ds-screen-title`, `.ds-question`,
 * `.ds-section-title` (+ `--lg`, `--sm`), `.ds-section-head`, `.ds-helper`,
 * `.ds-section-label`.
 *
 * Doctrine: each of these is a fixed semantic tag plus one class, and none of
 * them takes a prop that swaps the underlying element. Which component you pick
 * IS the semantic decision, which is what keeps screens from growing a broken
 * heading outline. Sizes come from the CSS scale, never from local overrides.
 */

/**
 * Kicker, the uppercase letterspaced eyebrow above a title.
 * CSS selector: `.ds-kicker`. The card-level kicker is a different atom and
 * lives on `<PriceStack kicker>`, so do not reach for this inside a card.
 */
export const Kicker = React.forwardRef(function Kicker({ className, ...rest }, ref) {
  return <p ref={ref} className={cn('ds-kicker', className)} {...rest} />;
});

/**
 * ScreenTitle, the modest 24px screen header.
 * CSS selector: `.ds-screen-title`. Doctrine: label the screen well without
 * being in your face, so this stays deliberately smaller than a question.
 */
export const ScreenTitle = React.forwardRef(function ScreenTitle(
  { className, as: Tag = 'h1', ...rest },
  ref,
) {
  return <Tag ref={ref} className={cn('ds-screen-title', className)} {...rest} />;
});

/**
 * Question, the 28px spoken question headline.
 * CSS selector: `.ds-question`. Doctrine: question weight is 700 (canon
 * decision #4, which settled the 600 versus 700 fork). Reserved for the
 * one-question-at-a-time onboarding and survey flows; it is not a generic
 * heading.
 */
export const Question = React.forwardRef(function Question(
  { className, as: Tag = 'h1', ...rest },
  ref,
) {
  return <Tag ref={ref} className={cn('ds-question', className)} {...rest} />;
});

/**
 * SectionTitle, the in-page section header.
 * CSS selector: `.ds-section-title` (+ `--lg` for account form sections,
 * `--sm` for filter group titles).
 */
const SECTION_TITLE_SIZE = {
  base: null,
  lg: 'ds-section-title--lg',
  sm: 'ds-section-title--sm',
};

export const SectionTitle = React.forwardRef(function SectionTitle(
  { className, size = 'base', as: Tag = 'h2', ...rest },
  ref,
) {
  return (
    <Tag
      ref={ref}
      className={cn('ds-section-title', SECTION_TITLE_SIZE[size], className)}
      {...rest}
    />
  );
});

/**
 * SectionHead, a section title and a trailing ghost link on one baseline.
 * CSS selector: `.ds-section-head`. The CSS collapses the nested section
 * title's own bottom margin, so nothing needs to be passed to the child.
 */
export const SectionHead = React.forwardRef(function SectionHead({ className, ...rest }, ref) {
  return <div ref={ref} className={cn('ds-section-head', className)} {...rest} />;
});

/**
 * Helper, the de-emphasised line that explains what happens next.
 * CSS selector: `.ds-helper`. A nested `<strong>` is styled by the CSS for the
 * one phrase worth lifting, such as the address a code was sent to.
 */
export const Helper = React.forwardRef(function Helper({ className, ...rest }, ref) {
  return <p ref={ref} className={cn('ds-helper', className)} {...rest} />;
});

/**
 * SectionLabel, the muted uppercase centred footer label.
 * CSS selector: `.ds-section-label`.
 */
export const SectionLabel = React.forwardRef(function SectionLabel({ className, ...rest }, ref) {
  return <p ref={ref} className={cn('ds-section-label', className)} {...rest} />;
});
